import Box from '@mui/material/Box';
import React from 'react';

import { colors, shadows } from './KestrixDesignSystem.jsx';

/**
 * Reusable instruction tooltip component for displaying contextual help
 * Used in 3D viewers, complex interfaces, and guided workflows
 */
const KestrixInstructionTooltip = ({
  message,
  visible = true,
  onClose,
  position = 'top-center', // 'top-center', 'bottom-center', 'top-left', 'top-right', etc.
  variant = 'dark', // 'dark', 'light'
  showCloseButton = true,
  animate = true,
  offset = 20, // Distance from edge
  zIndex = 1001,
  ...props
}) => {
  if (!visible) return null;

  // Position configurations
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      zIndex
    };

    switch (position) {
      case 'top-center':
        return {
          ...baseStyles,
          top: offset,
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'bottom-center':
        return {
          ...baseStyles,
          bottom: offset,
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'top-left':
        return {
          ...baseStyles,
          top: offset,
          left: offset
        };
      case 'top-right':
        return {
          ...baseStyles,
          top: offset,
          right: offset
        };
      case 'bottom-left':
        return {
          ...baseStyles,
          bottom: offset,
          left: offset
        };
      case 'bottom-right':
        return {
          ...baseStyles,
          bottom: offset,
          right: offset
        };
      case 'center':
        return {
          ...baseStyles,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
      default:
        return baseStyles;
    }
  };

  // Variant styles
  const getVariantStyles = () => {
    switch (variant) {
              case 'light':
          return {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: colors.text.body,
            border: `1px solid ${colors.border.medium}`,
            boxShadow: shadows.consistent
          };
              case 'dark':
        default:
          return {
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            boxShadow: shadows.consistent
          };
    }
  };

  // Animation styles
  const animationStyles = animate ? {
    animation: 'fadeInUp 0.3s ease-out',
    '@keyframes fadeInUp': {
      from: {
        opacity: 0,
        transform: position.includes('center') 
          ? 'translateX(-50%) translateY(10px)' 
          : 'translateY(10px)'
      },
      to: {
        opacity: 1,
        transform: position.includes('center')
          ? 'translateX(-50%) translateY(0)'
          : 'translateY(0)'
      }
    }
  } : {};

  const containerStyles = {
    ...getPositionStyles(),
    ...animationStyles
  };

  const tooltipStyles = {
    ...getVariantStyles(),
    padding: '8px 16px',
    borderRadius: 2,
    fontSize: '0.875rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    maxWidth: '300px'
  };

  return (
    <Box sx={containerStyles} {...props}>
      <Box sx={tooltipStyles}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {message}
        </span>
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '2px',
              transition: 'all 0.2s ease',
              minWidth: '16px',
              minHeight: '16px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = variant === 'dark' 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </Box>
    </Box>
  );
};

// Inject animation styles globally if not already present
if (typeof document !== 'undefined' && !document.getElementById('kestrix-instruction-tooltip-animations')) {
  const style = document.createElement('style');
  style.id = 'kestrix-instruction-tooltip-animations';
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

export default KestrixInstructionTooltip;
